import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import BrandMonogram, { WordmarkLockup } from "@/components/BrandMonogram";
import ThemeToggleButton from "@/components/ThemeToggleButton";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const resp = await login(form.email.trim(), form.password);
      if (resp.user?.force_password_change && resp.password_reset_token) {
        navigate(`/reset-password?token=${encodeURIComponent(resp.password_reset_token)}`);
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  }

  // Inline tokens for the always-dark left editorial panel.
  // These are intentionally fixed (not theme-bound) so the editorial split
  // keeps its drama regardless of whether the right form pane is light or dark.
  const onDarkFg = "#F5F5F4"; // porcelain
  const onDarkMuted = "rgba(245, 245, 244, 0.74)";
  const onDarkSubtle = "rgba(245, 245, 244, 0.50)";

  return (
    <div
      className="min-h-screen grid lg:grid-cols-[1fr_480px] relative"
      data-testid="login-page"
      style={{ background: "var(--bf-bg)", color: "var(--bf-fg)" }}
    >
      <ThemeToggleButton />

      <div
        className="hidden lg:flex relative overflow-hidden"
        style={{
          background: "var(--bf-graphite)",
          borderRight: "1px solid rgba(245, 245, 244, 0.08)",
          color: onDarkFg,
        }}
      >
        {/* Ambient geometric composition */}
        <div
          className="absolute inset-0 opacity-70"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, rgba(109,93,246,0.35), transparent 45%), radial-gradient(circle at 85% 80%, rgba(216,199,163,0.18), transparent 40%)",
          }}
        />
        <div className="absolute inset-0 bf-canvas opacity-50" />
        <div className="relative z-10 flex flex-col justify-between p-14 w-full">
          <WordmarkLockup tone="light-on-dark" />
          <div className="max-w-[480px]">
            <div
              className="text-[11px] font-mono uppercase tracking-[0.28em] mb-5"
              style={{ color: "var(--bf-champagne)" }}
            >
              01 / Brand Operating System
            </div>
            <h1
              className="font-editorial text-[72px] leading-[0.95] tracking-[-0.02em] mb-6"
              style={{ color: onDarkFg }}
            >
              Structured{" "}
              <em className="not-italic" style={{ color: "var(--bf-violet)" }}>
                identity
              </em>{" "}
              systems,
              <br />
              generated
              <br />in hours.
            </h1>
            <p className="text-[15px] leading-[1.7] max-w-[420px]" style={{ color: onDarkMuted }}>
              A subscription brand operating system for freelancers and boutique studios.
              Discovery, generation, editorial brand guides, export-ready client packs – end to end.
            </p>
          </div>
          <div
            className="flex items-center gap-6 text-[10px] font-mono uppercase tracking-[0.28em]"
            style={{ color: onDarkSubtle }}
          >
            <span>Phase 1</span>
            <span style={{ opacity: 0.4 }}>/</span>
            <span>Not a logo generator</span>
            <span style={{ opacity: 0.4 }}>/</span>
            <span>Real SVG · Real Claude · Real exports</span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-8 py-12 relative">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 flex items-center justify-between">
            <BrandMonogram />
          </div>
          <div
            className="text-[10px] font-mono uppercase tracking-[0.28em] mb-3"
            style={{ color: "var(--bf-fg-subtle)" }}
          >
            SIGN IN
          </div>
          <h2 className="text-[38px] font-editorial leading-[1.05] mb-10">Welcome back.</h2>
          <form onSubmit={onSubmit} className="space-y-6" data-testid="login-form">
            <div>
              <label
                htmlFor="login-email"
                className="block text-[10px] font-mono uppercase tracking-[0.22em] mb-2"
                style={{ color: "var(--bf-fg-subtle)" }}
              >
                EMAIL
              </label>
              <input
                id="login-email"
                name="email"
                type="email"
                autoComplete="email"
                required
                autoFocus
                data-testid="login-email-input"
                className="w-full bg-transparent outline-none px-0 py-2.5 text-[15px] transition-colors"
                style={{
                  color: "var(--bf-fg)",
                  borderBottom: "1px solid var(--bf-line-medium)",
                }}
                onFocus={(e) => (e.target.style.borderBottomColor = "var(--bf-violet)")}
                onBlur={(e) => (e.target.style.borderBottomColor = "var(--bf-line-medium)")}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@studio.com"
              />
            </div>
            <div>
              <label
                htmlFor="login-password"
                className="block text-[10px] font-mono uppercase tracking-[0.22em] mb-2"
                style={{ color: "var(--bf-fg-subtle)" }}
              >
                PASSWORD
              </label>
              <input
                id="login-password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                data-testid="login-password-input"
                className="w-full bg-transparent outline-none px-0 py-2.5 text-[15px] transition-colors"
                style={{
                  color: "var(--bf-fg)",
                  borderBottom: "1px solid var(--bf-line-medium)",
                }}
                onFocus={(e) => (e.target.style.borderBottomColor = "var(--bf-violet)")}
                onBlur={(e) => (e.target.style.borderBottomColor = "var(--bf-line-medium)")}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••"
              />
            </div>
            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-[11px] font-mono"
                style={{ color: "var(--bf-fg-subtle)" }}
              >
                Forgot password?
              </Link>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="bf-btn-primary w-full font-semibold text-sm px-4 py-3.5 rounded-md disabled:opacity-50 transition-all"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <div className="mt-8 text-sm" style={{ color: "var(--bf-fg-muted)" }}>
            New to BrandFinish?{" "}
            <Link
              to="/register"
              data-testid="link-to-register"
              className="underline underline-offset-4 decoration-[var(--bf-violet)] decoration-2 hover:decoration-[var(--bf-violet-dark)]"
              style={{ color: "var(--bf-fg)" }}
            >
              Create an account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
